import { useMemo } from "react";
import _ from "lodash";
import React, { ReactNode } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { COLOR_PRIMARY } from "../../../constants/theme";
import { useFacebookCore } from "../../../cores/facebook";
import { useFacebookAdsCore } from "../../../cores/facebookAds";
import { useGoogleCore } from "../../../cores/google";
import { useInstagramCore } from "../../../cores/instagram";
import { useTiktokCore } from "../../../cores/tiktok";
import { isYoutubeConnected, useYoutubeCore } from "../../../cores/youtube";
import { isYoutubePostingEnabled } from "../../../cores/config/featureFlags";
import { useJumpsellerCore } from "../../../cores/jumpseller";
import { useMeCore } from "../../../cores/me";
import { useMercadolibreCore } from "../../../cores/mercadolibre";
import { useRouterCore } from "../../../cores/router";
import { useWooCommerceCore } from "../../../cores/wooCommerce";
import { JumpsellerIcon } from "../social/Jumpseller/JumpsellerIcon";
import { MercadolibreIcon } from "../social/Mercadolibre/MercadolibreIcon";
import { WooCommerceIcon } from "../social/WooCommerce/WooCommerceIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import { IconName, IconPrefix } from "@fortawesome/fontawesome-svg-core";
import cn from "classnames";

export interface SidebarSubItem {
  activeUrl: string;
  onClick: () => void;
  label: string;
  iconName?: IconName | undefined;
  disabled?: boolean | undefined;
  iconPrefix?: IconPrefix | undefined;
  disabledMessage?: string | undefined;
  icon?: ReactNode;
}

interface Props {
  planNotAllowedMessage: string;
}

const SidebarSubItems = ({ planNotAllowedMessage }: Props) => {
  const {
    routes,
    goToSocialPost,
    goToSocialAutomatedPosts,
    goToAIGeneration,
    goToTextToImage,
    goToSocialHistory,
    goToSocialContentGrid,
    goToAnalyticsSummary,
    goToInstagramAnalytics,
    goToTiktokAnalytics,
    goToYoutubeAnalytics,
    goToFacebookAnalytics,
    goToWooCommerceAnalytics,
    goToGoogleAnalytics,
    goToWebForms,
    goToFacebookForms,
    goToEditWebChat,
    goToWebChat,
    goToWooCommerceOrders,
    goToEditWhatsappChat,
    goToMercadolibreOrders,
    goToFacebookCampaigns,
    goToMercadolibreAnalytics,
    goToContacts,
    goToContactCompanies,
    goToCalendar,
    goToSocialCalendar,
    goToSocialStory,
    goToVideoGen,
    goToVideoEditor,
    goToBusinessBoard,
    goToBusinessBoardAnalytics,
    goToJumpsellerOrders,
    goToTasks,
  } = useRouterCore();

  const intl = useIntl();
  const { companyPlan } = useMeCore();
  const {
    rules: {
      allowWebChat = false,
      allowWhatsappChat = false,
      allowFacebookAdvertisements = false,
      webFormsAmount = 0,
    } = {},
  } = companyPlan! || { name: "." };

  const { pathname } = useLocation();
  const { settings: facebookSettings } = useFacebookCore();
  const { settings: facebookAdsSettings } = useFacebookAdsCore();
  const { settings: googleSettings } = useGoogleCore();
  const { settings: instagramSettings } = useInstagramCore();
  const { settings: tiktokSettings } = useTiktokCore();
  const { settings: youtubeSettings } = useYoutubeCore();
  const { settings: wooSettings } = useWooCommerceCore();
  const { settings: mercadolibreSettings } = useMercadolibreCore();
  const { settings: jumpsellerSettings } = useJumpsellerCore();
  const isFacebookSync = !_.isEmpty(facebookSettings);
  const isFacebookAdsSync = !_.isEmpty(facebookAdsSettings);
  const isInstagramSync = !_.isEmpty(instagramSettings);
  const isTikTokSync = !_.isEmpty(tiktokSettings);
  const isYoutubeSync =
    isYoutubePostingEnabled() && isYoutubeConnected(youtubeSettings);
  const isGoogleSync = !_.isEmpty(googleSettings);
  const isWooSync = !_.isEmpty(wooSettings);
  const isMercadolibreSync = !_.isEmpty(mercadolibreSettings);
  const isJumpsellerSync = !_.isEmpty(jumpsellerSettings);
  const isWebFormAllow = webFormsAmount > 0;
  const planName = companyPlan?.name ?? ".";
  const isPremiumPlan = planName.split(".")[2] === "Premium";

  const socialSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.socialPost,
        onClick: () => goToSocialPost(""),
        label: intl.formatMessage({ id: "app.sidebar.socialMediaPost" }),
        iconName: "pen-to-square",
      },
      {
        activeUrl: routes.socialAutomatedPosts,
        onClick: goToSocialAutomatedPosts,
        label: `${intl.formatMessage({
          id: "app.sidebar.socialMediaAutomatedPosts",
        })} (${intl.formatMessage({ id: "app.sidebar.comingSoon" })})`,
        icon: (
          <FontAwesomeIcon
            icon={faWandMagicSparkles}
            style={{ color: COLOR_PRIMARY }}
            className="item-icon"
          />
        ),
        disabled: true,
      },
      // {
      //   activeUrl: routes.socialStory,
      //   onClick: () => goToSocialStory(""),
      //   label: intl.formatMessage({ id: "app.sidebar.socialStory" }),
      //   iconName: "history",
      // },
      {
        activeUrl: routes.socialContentGrid,
        onClick: goToSocialContentGrid,
        label: intl.formatMessage({ id: "app.sidebar.socialContentGrid" }),
        iconName: "table",
      },
      {
        activeUrl: routes.socialCalendar,
        onClick: goToSocialCalendar,
        label: intl.formatMessage({ id: "app.sidebar.socialMediaCalendar" }),
        iconName: "calendar",
      },
      {
        activeUrl: routes.socialHistory,
        onClick: goToSocialHistory,
        label: intl.formatMessage({ id: "app.sidebar.socialMediaRecord" }),
        iconName: "history",
      },
      {
        activeUrl: routes.facebookCampaigns,
        onClick: goToFacebookCampaigns,
        label: intl.formatMessage({ id: "app.sidebar.socialMediaMetaAds" }),
        iconName: "meta",
        iconPrefix: "fab",
        disabled:
          !allowFacebookAdvertisements || !isFacebookAdsSync,
        disabledMessage: !allowFacebookAdvertisements
          ? planNotAllowedMessage
          : undefined,
      },
    ];
  }, [
    routes.socialPost,
    routes.socialAutomatedPosts,
    routes.socialStory,
    routes.socialContentGrid,
    routes.socialCalendar,
    routes.socialHistory,
    routes.facebookCampaigns,
    allowFacebookAdvertisements,
    isFacebookAdsSync,
    intl,
    goToSocialHistory,
    goToSocialContentGrid,
    goToSocialCalendar,
    goToSocialStory,
    goToSocialPost,
    goToSocialAutomatedPosts,
    goToFacebookCampaigns,
    planNotAllowedMessage,
  ]);

  // IA: Video Gen + Editor de imágenes (dentro del mismo menú)
  const iaSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.aiTextToImage,
        onClick: goToTextToImage,
        label: intl.formatMessage({ id: "app.sidebar.textToImage" }),
        iconName: "image",
        disabled: !isPremiumPlan,
        disabledMessage: planNotAllowedMessage,
      },
      {
        activeUrl: routes.aiGeneration,
        onClick: goToAIGeneration,
        label: intl.formatMessage({ id: "app.sidebar.aiEditorImages" }),
        iconName: "magic",
        iconPrefix: "fas",
        disabled: !isPremiumPlan,
        disabledMessage: planNotAllowedMessage,
      },
      {
        activeUrl: routes.videoGen,
        onClick: goToVideoGen,
        label: `${intl.formatMessage({
          id: "app.sidebar.ia.videoGen",
        })} (${intl.formatMessage({ id: "app.sidebar.comingSoon" })})`,
        iconName: "video",
        disabled: true,
      },
      {
        activeUrl: routes.videoEditor,
        onClick: goToVideoEditor,
        label: `${intl.formatMessage({
          id: "app.sidebar.ia.videoEditor",
        })} (${intl.formatMessage({ id: "app.sidebar.comingSoon" })})`,
        iconName: "film",
        disabled: true,
      },
    ];
  }, [
    routes.videoGen,
    routes.videoEditor,
    routes.aiGeneration,
    routes.aiTextToImage,
    intl,
    goToVideoGen,
    goToVideoEditor,
    goToAIGeneration,
    goToTextToImage,
    isPremiumPlan,
    planNotAllowedMessage,
  ]);

  const advertisementsSubItems: SidebarSubItem[] = useMemo(() => [], []);

  const formsSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.webForms,
        onClick: goToWebForms,
        label: intl.formatMessage({ id: "app.sidebar.forms.Web" }),
        iconName: "wpforms",
        iconPrefix: "fab",
        disabled: !isWebFormAllow,
      },
      {
        activeUrl: routes.facebookForm,
        onClick: goToFacebookForms,
        label: intl.formatMessage({ id: "app.sidebar.forms.Facebook" }),
        iconName: "facebook",
        iconPrefix: "fab",
        disabled: !isFacebookSync,
      },
    ];
  }, [
    routes.webForms,
    routes.facebookForm,
    isWebFormAllow,
    isFacebookSync,
    intl,
    goToFacebookForms,
    goToWebForms
  ]);

  const chatSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.webChat,
        onClick: goToWebChat,
        label: intl.formatMessage({ id: "app.sidebar.chat.conversations" }),
        iconName: "comments",
        disabled: !allowWebChat,
        disabledMessage: planNotAllowedMessage,
      },
      {
        activeUrl: routes.editWebChat,
        onClick: goToEditWebChat,
        label: intl.formatMessage({ id: "app.sidebar.chat.WebChatConfig" }),
        iconName: "cog",
        disabled: !allowWebChat,
        disabledMessage: planNotAllowedMessage,
      },
      {
        activeUrl: routes.editWhatsappChat,
        onClick: goToEditWhatsappChat,
        label: intl.formatMessage({
          id: "app.sidebar.chat.whatsappConfig",
        }),
        iconPrefix: "fab",
        iconName: "whatsapp",
        disabled: !allowWhatsappChat,
        disabledMessage: planNotAllowedMessage,
      },
    ];
  }, [
    routes.webChat,
    routes.editWebChat,
    routes.editWhatsappChat,
    allowWebChat,
    allowWhatsappChat,
    planNotAllowedMessage,
    intl,
    goToWebChat,
    goToEditWhatsappChat,
    goToEditWebChat
  ]);

  const onlineSellsSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.wooCommerceOrders,
        onClick: goToWooCommerceOrders,
        label: intl.formatMessage({
          id: "app.sidebar.OnlineSales.WooCommerce",
        }),
        iconName: "store",
        disabled: !isWooSync,
      },
      {
        activeUrl: routes.mercadolibreOrders,
        onClick: goToMercadolibreOrders,
        label: intl.formatMessage({
          id: "app.sidebar.OnlineSales.Mercadolibre",
        }),
        iconName: "store",
        disabled: !isMercadolibreSync,
      },
      {
        activeUrl: routes.jumpsellerOrders,
        onClick: goToJumpsellerOrders,
        label: intl.formatMessage({
          id: "app.sidebar.OnlineSales.Jumpseller",
        }),
        iconName: "store",
        disabled: !isJumpsellerSync,
      },
    ];
  }, [
    routes.wooCommerceOrders,
    routes.jumpsellerOrders,
    routes.mercadolibreOrders,
    intl,
    isJumpsellerSync,
    isMercadolibreSync,
    isWooSync,
    pathname,
    goToJumpsellerOrders,
    goToMercadolibreOrders,
    goToWooCommerceOrders
  ]);

  const crmSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        onClick: goToTasks,
        label: intl.formatMessage({ id: "app.sidebar.tasks" }),
        iconName: "tasks",
        activeUrl: routes.tasks,
      },
      {
        activeUrl: routes.calendar,
        onClick: goToCalendar,
        label: intl.formatMessage({ id: "app.sidebar.meetings" }),
        iconName: "calendar",
      },
      {
        onClick: goToBusinessBoard,
        label: intl.formatMessage({ id: "app.sidebar.crm.business" }),
        iconName: "briefcase",
        activeUrl: routes.businessBoard,
      },
      {
        onClick: goToBusinessBoardAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.crm.metrics" }),
        iconName: "chart-bar",
        activeUrl: routes.businessBoardAnalytics,
      },
    ];
  }, [
    routes.businessBoardAnalytics,
    routes.businessBoard,
    routes.calendar,
    routes.tasks,
    intl,
    goToTasks,
    goToCalendar,
    goToBusinessBoard,
    goToBusinessBoardAnalytics
  ]);

  const databaseSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.contacts,
        onClick: goToContacts,
        label: intl.formatMessage({ id: "app.sidebar.contacts" }),
        iconName: "address-book",
      },
      {
        activeUrl: routes.contactCompanies,
        onClick: goToContactCompanies,
        label: intl.formatMessage({ id: "app.sidebar.contact.companies" }),
        iconName: "building",
      },
    ];
  }, [routes.contacts, routes.contactCompanies, intl, goToContactCompanies, goToContacts]);

  const metricsSubItems: SidebarSubItem[] = useMemo(() => {
    return [
      {
        activeUrl: routes.analyticsSummary,
        onClick: goToAnalyticsSummary,
        label: intl.formatMessage({ id: "app.sidebar.metrics.Summary" }),
        iconName: "clipboard-list",
      },
      {
        activeUrl: routes.facebookAnalytics,
        onClick: goToFacebookAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.Facebook" }),
        iconName: "facebook",
        iconPrefix: "fab",
      },
      {
        activeUrl: routes.instagramAnalytics,
        onClick: goToInstagramAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.Instagram" }),
        iconName: "instagram",
        iconPrefix: "fab",
        disabled: !isInstagramSync,
      },
      {
        activeUrl: routes.tiktokAnalytics,
        onClick: goToTiktokAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.TikTok" }),
        iconName: "tiktok",
        iconPrefix: "fab",
        disabled: !isTikTokSync,
      },
      {
        activeUrl: routes.youtubeAnalytics,
        onClick: goToYoutubeAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.YouTube" }),
        iconName: "youtube",
        iconPrefix: "fab",
        disabled: !isYoutubeSync,
      },
      {
        activeUrl: routes.googleAnalytics,
        onClick: goToGoogleAnalytics,
        label: intl.formatMessage({
          id: "app.sidebar.metrics.GoogleAnalytics",
        }),
        iconName: "google",
        iconPrefix: "fab",
        disabled: !isGoogleSync,
      },
      {
        activeUrl: routes.wooCommerceAnalytics,
        onClick: goToWooCommerceAnalytics,
        label: intl.formatMessage({
          id: "app.sidebar.metrics.WooCommerce",
        }),
        iconName: "chart-line",
        disabled: !isWooSync,
      },
      {
        activeUrl: routes.mercadolibreAnalytics,
        onClick: goToMercadolibreAnalytics,
        label: intl.formatMessage({ id: "app.sidebar.metrics.MercLibre" }),
        iconName: "chart-line",
        disabled: !isMercadolibreSync,
      },
    ];
  }, [
    routes.mercadolibreAnalytics,
    routes.wooCommerceAnalytics,
    routes.googleAnalytics,
    routes.analyticsSummary,
    routes.facebookAnalytics,
    routes.instagramAnalytics,
    routes.tiktokAnalytics,
    routes.youtubeAnalytics,
    intl,
    isMercadolibreSync,
    isGoogleSync,
    isInstagramSync,
    isTikTokSync,
    isYoutubeSync,
    isWooSync,
    pathname,
    goToMercadolibreAnalytics,
    goToWooCommerceAnalytics,
    goToAnalyticsSummary,
    goToFacebookAnalytics,
    goToGoogleAnalytics,
    goToInstagramAnalytics,
    goToTiktokAnalytics,
    goToYoutubeAnalytics
  ]);

  return {
    socialSubItems,
    iaSubItems,
    advertisementsSubItems,
    formsSubItems,
    chatSubItems,
    onlineSellsSubItems,
    crmSubItems,
    databaseSubItems,
    metricsSubItems,
  };
};

export default SidebarSubItems;
